import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Plus, Pencil, Trash2, LayoutGrid, Copy, Upload, UserX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";
import CtaButton from "@/components/CtaButton";
import ZonasSemanal from "@/components/ZonasSemanal";
import ZonasPlanDia from "@/components/ZonasPlanDia";
import { sugerirNuevoTramo } from "@/components/ZonasCobertura";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORES_ZONA = ["#8b5cf6", "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#6366f1"];

function sumarDias(iso, delta) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toLocaleDateString("sv");
}

function parseHoraMin(h) {
  const [hh, mm] = h.slice(0, 5).split(":");
  return Number(hh) * 60 + Number(mm);
}

function tramoFueraHorario(horaInicio, horaFin, franjas) {
  if (!franjas?.length) {
    return { fuera: true, mensaje: "El trabajador no tiene jornada prevista ese día" };
  }
  const ini = parseHoraMin(horaInicio);
  const fin = parseHoraMin(horaFin);
  if (fin <= ini) {
    return { fuera: true, mensaje: "La hora de fin debe ser posterior a la de inicio" };
  }
  const ordenadas = [...franjas].sort((a, b) => parseHoraMin(a.inicio) - parseHoraMin(b.inicio));
  let pos = ini;
  while (pos < fin) {
    const franja = ordenadas.find((f) => parseHoraMin(f.inicio) <= pos && pos < parseHoraMin(f.fin));
    if (!franja) {
      const txt = ordenadas.map((f) => `${f.inicio}–${f.fin}`).join(", ");
      return {
        fuera: true,
        mensaje: `El tramo ${horaInicio.slice(0, 5)}–${horaFin.slice(0, 5)} queda fuera del horario laboral (${txt})`,
      };
    }
    pos = Math.min(fin, parseHoraMin(franja.fin));
  }
  return { fuera: false, mensaje: null };
}

export default function ZonasPlanificacion() {
  const { user } = useAuth();
  const { empresa, cargando } = useEmpresa();
  const esAdmin = user?.rol === "admin";
  const [fecha, setFecha] = useState(hoyISO());
  const [plan, setPlan] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [solCambio, setSolCambio] = useState([]);
  const [publicando, setPublicando] = useState(false);
  const [acusesPendientes, setAcusesPendientes] = useState([]);
  const semanalRef = useRef(null);

  const cargarAcuses = useCallback(async () => {
    try {
      const { data } = await api.get("/zonas/planificacion/acuses-pendientes");
      setAcusesPendientes(data?.pendientes || []);
    } catch { /* ignore */ }
  }, []);

  const cargarSolCambio = useCallback(async () => {
    try {
      const { data } = await api.get("/zonas/solicitudes-cambio", { params: { pendientes: true } });
      setSolCambio(data || []);
    } catch { /* ignore */ }
  }, []);

  const [zonaOpen, setZonaOpen] = useState(false);
  const [editZona, setEditZona] = useState(null);
  const [zonaForm, setZonaForm] = useState({ nombre: "", descripcion: "", color: "#8b5cf6", centro_id: "", orden: 0 });

  const [asigOpen, setAsigOpen] = useState(false);
  const [editAsig, setEditAsig] = useState(null);
  const [empleadoAsig, setEmpleadoAsig] = useState(null);
  const [confirmFueraOpen, setConfirmFueraOpen] = useState(false);
  const [confirmFueraMsg, setConfirmFueraMsg] = useState("");
  const [dupOpen, setDupOpen] = useState(false);
  const [dupForm, setDupForm] = useState({
    usuario_id: "",
    empleado_nombre: "",
    fecha_origen: hoyISO(),
    asignacion_ids: [],
    usuarios_destino_ids: [],
    modo_destino: "dia",
    fecha_destino: hoyISO(),
    fecha_destino_desde: hoyISO(),
    fecha_destino_hasta: hoyISO(),
    sobrescribir: false,
  });
  const [dupResult, setDupResult] = useState(null);
  const [dupTramos, setDupTramos] = useState([]);
  const [empleadosDupLista, setEmpleadosDupLista] = useState([]);
  const [dupDiaOpen, setDupDiaOpen] = useState(false);
  const [dupDiaForm, setDupDiaForm] = useState({
    fecha_origen: hoyISO(),
    modo_destino: "dia",
    fecha_destino: hoyISO(),
    fecha_destino_desde: hoyISO(),
    fecha_destino_hasta: hoyISO(),
    sobrescribir: false,
  });
  const [dupPreview, setDupPreview] = useState(null);
  const [dupIncidenciasOpen, setDupIncidenciasOpen] = useState(false);
  const [dupPendiente, setDupPendiente] = useState(null);
  const [asigForm, setAsigForm] = useState({
    usuario_id: "",
    zona_id: "",
    fecha: hoyISO(),
    hora_inicio: "09:00",
    hora_fin: "14:00",
    notas: "",
  });
  const [empleadosAsignables, setEmpleadosAsignables] = useState([]);

  const cargarPlan = useCallback(async () => {
    try {
      const { data } = await api.get("/zonas/planificacion", { params: { fecha } });
      setPlan(data);
      setZonas(data.zonas || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [fecha]);

  const cargarZonas = useCallback(async () => {
    try {
      const { data } = await api.get("/zonas", { params: { activas: false } });
      setZonas(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    cargarPlan();
    cargarSolCambio();
    cargarAcuses();
  }, [cargarPlan, cargarSolCambio, cargarAcuses]);

  const revisarCambioZona = async (id, accion) => {
    try {
      await api.put(`/zonas/solicitudes-cambio/${id}/revisar`, { accion, comentario: "" });
      toast.success(accion === "aprobar" ? "Cambio aplicado en la planificación" : "Solicitud rechazada");
      cargarSolCambio();
      cargarPlan();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publicarDia = async () => {
    if (!window.confirm(
      `¿Publicar los cambios del ${fmtFecha(fecha)}? Solo se notificará a los trabajadores con cambios reales.`
    )) return;
    setPublicando(true);
    try {
      const { data } = await api.post("/zonas/planificacion/publicar", { desde: fecha, hasta: fecha });
      const n = data?.notificados ?? 0;
      toast.success(n > 0 ? `Publicado: ${n} trabajador${n !== 1 ? "es" : ""} notificado${n !== 1 ? "s" : ""}` : "Publicado (sin cambios que notificar)");
      cargarPlan();
      cargarAcuses();
      semanalRef.current?.cargar?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublicando(false);
    }
  };

  useEffect(() => {
    if (!dupOpen || !dupForm.usuario_id) return;
    api
      .get("/zonas/asignaciones", {
        params: { fecha: dupForm.fecha_origen, usuario_id: Number(dupForm.usuario_id) },
      })
      .then((r) => setDupTramos(r.data))
      .catch(() => setDupTramos([]));
  }, [dupOpen, dupForm.fecha_origen, dupForm.usuario_id]);

  useEffect(() => {
    if (esAdmin) {
      api.get("/centros").then((r) => setCentros(r.data)).catch(() => {});
    }
  }, [esAdmin]);

  const abrirZona = (z = null) => {
    setEditZona(z);
    setZonaForm(z
      ? { nombre: z.nombre, descripcion: z.descripcion || "", color: z.color, centro_id: z.centro_id ? String(z.centro_id) : "", orden: z.orden || 0 }
      : { nombre: "", descripcion: "", color: COLORES_ZONA[zonas.length % COLORES_ZONA.length], centro_id: "", orden: zonas.length });
    setZonaOpen(true);
  };

  const guardarZona = async () => {
    if (!zonaForm.nombre.trim()) {
      toast.error("Indica el nombre de la zona");
      return;
    }
    try {
      const payload = {
        nombre: zonaForm.nombre.trim(),
        descripcion: zonaForm.descripcion,
        color: zonaForm.color,
        orden: Number(zonaForm.orden) || 0,
        centro_id: zonaForm.centro_id ? Number(zonaForm.centro_id) : null,
      };
      if (editZona) {
        await api.put(`/zonas/catalogo/${editZona.id}`, payload);
        toast.success("Zona actualizada");
      } else {
        await api.post("/zonas", payload);
        toast.success("Zona creada");
      }
      setZonaOpen(false);
      cargarZonas();
      cargarPlan();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminarZona = async (z) => {
    if (!window.confirm(`¿Desactivar la zona "${z.nombre}"?`)) return;
    try {
      await api.delete(`/zonas/catalogo/${z.id}`);
      toast.success("Zona desactivada");
      cargarZonas();
      cargarPlan();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resolverEmpleado = (empleados, usuarioId, fechaDia) => {
    const emp = (empleados || []).find((e) => e.id === Number(usuarioId));
    if (!emp) return null;
    if (emp.dias) {
      const dia = emp.dias.find((d) => d.fecha === fechaDia);
      return { ...emp, horario: dia?.horario || emp.horario };
    }
    return emp;
  };

  const abrirAsignacion = (empleado, tramo = null, horaSugerida = null, fechaDia = null) => {
    const f = fechaDia || fecha;
    setEditAsig(tramo);
    setEmpleadoAsig(empleado);
    setEmpleadosAsignables(plan?.empleados?.length ? plan.empleados : [empleado]);
    if (tramo) {
      setAsigForm({
        usuario_id: String(empleado.id),
        zona_id: String(tramo.zona_id),
        fecha: f,
        hora_inicio: tramo.hora_inicio,
        hora_fin: tramo.hora_fin,
        notas: tramo.notas || "",
      });
    } else {
      const tramosDia = empleado.dias
        ? empleado.dias.find((d) => d.fecha === f)?.tramos || []
        : empleado.tramos || [];
      const sugerido = sugerirNuevoTramo(tramosDia, plan?.zonas || zonas);
      const hi = horaSugerida != null ? `${String(horaSugerida).padStart(2, "0")}:00` : sugerido.horaInicio;
      const hf = horaSugerida != null
        ? `${String(Math.min(horaSugerida + 4, 24)).padStart(2, "0")}:00`
        : sugerido.horaFin;
      setAsigForm({
        usuario_id: String(empleado.id),
        zona_id: sugerido.zonaId,
        fecha: f,
        hora_inicio: hi,
        hora_fin: hf,
        notas: "",
      });
    }
    setAsigOpen(true);
  };

  const abrirAsignacionDesdeZona = (zona, fechaDia, empleados, info) => {
    const sugerido = sugerirNuevoTramo(info?.tramos || [], plan?.zonas || zonas);
    setEditAsig(null);
    setEmpleadoAsig(null);
    setEmpleadosAsignables(empleados || plan?.empleados || []);
    setAsigForm({
      usuario_id: "",
      zona_id: String(zona.id),
      fecha: fechaDia,
      hora_inicio: sugerido.horaInicio,
      hora_fin: sugerido.horaFin,
      notas: "",
    });
    setAsigOpen(true);
  };

  const editarTramoDesdeCobertura = (tramo, empleados, fechaDia) => {
    const emp = resolverEmpleado(empleados, tramo.usuario_id, fechaDia || tramo.fecha);
    if (!emp) {
      toast.error("No se encontró el trabajador del tramo");
      return;
    }
    setEmpleadosAsignables(empleados || plan?.empleados || []);
    abrirAsignacion(emp, tramo, null, fechaDia || tramo.fecha);
  };

  const guardarAsignacion = async (confirmarFueraHorario = false) => {
    if (!asigForm.usuario_id || !asigForm.zona_id) {
      toast.error("Selecciona trabajador y zona");
      return;
    }
    const hi = asigForm.hora_inicio.slice(0, 5);
    const hf = asigForm.hora_fin.slice(0, 5);
    const empHorario = empleadoAsig || resolverEmpleado(empleadosAsignables, asigForm.usuario_id, asigForm.fecha);

    if (!confirmarFueraHorario && empHorario?.horario) {
      if (empHorario.horario.ausencia) {
        setConfirmFueraMsg(
          `El trabajador tiene ${empHorario.horario.ausencia.label} ese día. ¿Deseas asignar el tramo igualmente?`
        );
        setConfirmFueraOpen(true);
        return;
      }
      const check = tramoFueraHorario(hi, hf, empHorario.horario.franjas);
      if (check.fuera) {
        setConfirmFueraMsg(`${check.mensaje}. ¿Deseas asignar el tramo igualmente?`);
        setConfirmFueraOpen(true);
        return;
      }
    }

    try {
      const payload = {
        usuario_id: Number(asigForm.usuario_id),
        zona_id: Number(asigForm.zona_id),
        fecha: asigForm.fecha,
        hora_inicio: hi,
        hora_fin: hf,
        notas: asigForm.notas,
        confirmar_fuera_horario: confirmarFueraHorario,
      };
      if (editAsig) {
        await api.put(`/zonas/asignaciones/${editAsig.id}`, payload);
        toast.success("Asignación actualizada — el trabajador será notificado");
      } else {
        await api.post("/zonas/asignaciones", payload);
        toast.success("Asignación creada — el trabajador será notificado");
      }
      setConfirmFueraOpen(false);
      setAsigOpen(false);
      cargarPlan();
      semanalRef.current?.cargar();
    } catch (e) {
      if (e.response?.status === 409 && !confirmarFueraHorario) {
        setConfirmFueraMsg(`${formatApiError(e)} ¿Deseas asignar el tramo igualmente?`);
        setConfirmFueraOpen(true);
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  const avisoHorario = (() => {
    const emp = empleadoAsig || resolverEmpleado(empleadosAsignables, asigForm.usuario_id, asigForm.fecha);
    if (!emp?.horario || !asigForm.hora_inicio || !asigForm.hora_fin) return null;
    if (emp.horario.ausencia) {
      return { tipo: "ausencia", mensaje: emp.horario.ausencia.label };
    }
    const check = tramoFueraHorario(
      asigForm.hora_inicio.slice(0, 5),
      asigForm.hora_fin.slice(0, 5),
      emp.horario.franjas
    );
    return check.fuera ? { tipo: "fuera", mensaje: check.mensaje } : null;
  })();

  const eliminarAsignacion = async (tramo) => {
    if (!window.confirm("¿Eliminar este tramo? El trabajador recibirá un aviso.")) return;
    try {
      await api.delete(`/zonas/asignaciones/${tramo.id}`);
      toast.success("Asignación eliminada");
      cargarPlan();
      semanalRef.current?.cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const tramosDeEmpleado = (empleado, f) => {
    if (empleado.dias) {
      return empleado.dias.find((d) => d.fecha === f)?.tramos || [];
    }
    return empleado.tramos || [];
  };

  const abrirDuplicar = (empleado, tramoIds = null, fechaDia = null, empleadosLista = null) => {
    const f = fechaDia || fecha;
    const tramos = tramosDeEmpleado(empleado, f);
    const ids = tramoIds ?? tramos.map((t) => t.id);
    if (!ids.length) {
      toast.error("No hay tramos para duplicar");
      return;
    }
    setEmpleadosDupLista(empleadosLista || plan?.empleados || []);
    setDupForm({
      usuario_id: String(empleado.id),
      empleado_nombre: empleado.nombre,
      fecha_origen: f,
      asignacion_ids: ids,
      usuarios_destino_ids: [],
      modo_destino: "dia",
      fecha_destino: sumarDias(f, 1),
      fecha_destino_desde: sumarDias(f, 1),
      fecha_destino_hasta: sumarDias(f, 1),
      sobrescribir: false,
    });
    setDupResult(null);
    setDupPreview(null);
    setDupOpen(true);
  };

  const abrirDuplicarDia = () => {
    const tramosDia = plan?.empleados?.reduce((n, e) => n + (e.tramos?.length || 0), 0) || 0;
    if (!tramosDia) {
      toast.error("No hay tramos en este día para duplicar");
      return;
    }
    setDupDiaForm({
      fecha_origen: fecha,
      modo_destino: "dia",
      fecha_destino: sumarDias(fecha, 1),
      fecha_destino_desde: sumarDias(fecha, 1),
      fecha_destino_hasta: sumarDias(fecha, 1),
      sobrescribir: false,
    });
    setDupPreview(null);
    setDupDiaOpen(true);
  };

  const buildDupPayload = (form, { allDay = false, confirmarIncidencias = false } = {}) => {
    const payload = {
      fecha_origen: form.fecha_origen,
      sobrescribir: form.sobrescribir,
      confirmar_incidencias: confirmarIncidencias,
    };
    if (!allDay) {
      payload.usuario_id = Number(form.usuario_id);
      payload.asignacion_ids = form.asignacion_ids;
      if (form.usuarios_destino_ids?.length) {
        payload.usuarios_destino_ids = form.usuarios_destino_ids.map(Number);
      }
    }
    if (form.modo_destino === "dia") {
      payload.fecha_destino = form.fecha_destino;
    } else {
      payload.fecha_destino_desde = form.fecha_destino_desde;
      payload.fecha_destino_hasta = form.fecha_destino_hasta;
    }
    return payload;
  };

  const previewDuplicacion = async (payload) => {
    const { data } = await api.post("/zonas/asignaciones/duplicar/preview", payload);
    return data;
  };

  const ejecutarDuplicacion = async (payload, { cerrar = null } = {}) => {
    const { data } = await api.post("/zonas/asignaciones/duplicar", payload);
    setDupResult(data);
    toast.success(`${data.creadas} tramo(s) duplicado(s)`);
    if (data.omitidas?.length) {
      toast.warning(`${data.omitidas.length} omitido(s)`);
    }
    cargarPlan();
    semanalRef.current?.cargar();
    if (cerrar) cerrar();
    return data;
  };

  const iniciarDuplicacion = async (form, { allDay = false, cerrar = null } = {}) => {
    const payload = buildDupPayload(form, { allDay, confirmarIncidencias: false });
    try {
      const preview = await previewDuplicacion(payload);
      setDupPreview(preview);
      if (preview.incidencias?.length > 0) {
        setDupPendiente({ payload, cerrar });
        setDupIncidenciasOpen(true);
        return;
      }
      await ejecutarDuplicacion(payload, { cerrar });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmarDuplicarConIncidencias = async (incluirIncidencias) => {
    if (!dupPendiente) return;
    setDupIncidenciasOpen(false);
    try {
      const payload = {
        ...dupPendiente.payload,
        confirmar_incidencias: incluirIncidencias,
      };
      await ejecutarDuplicacion(payload, { cerrar: dupPendiente.cerrar });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDupPendiente(null);
    }
  };

  const duplicarTramos = () => iniciarDuplicacion(dupForm, { allDay: false, cerrar: () => setDupOpen(false) });
  const duplicarDiaCompleto = () => iniciarDuplicacion(dupDiaForm, { allDay: true, cerrar: () => setDupDiaOpen(false) });

  const toggleTramoDup = (id) => {
    setDupForm((f) => {
      const sel = f.asignacion_ids.includes(id)
        ? f.asignacion_ids.filter((x) => x !== id)
        : [...f.asignacion_ids, id];
      return { ...f, asignacion_ids: sel };
    });
  };

  const toggleDestinoDup = (id) => {
    setDupForm((f) => {
      const sel = f.usuarios_destino_ids.includes(id)
        ? f.usuarios_destino_ids.filter((x) => x !== id)
        : [...f.usuarios_destino_ids, id];
      return { ...f, usuarios_destino_ids: sel };
    });
  };

  const seleccionarTodosTramosDup = () => {
    setDupForm((f) => ({ ...f, asignacion_ids: dupTramos.map((t) => t.id) }));
  };

  const esDuplicarDiaCompleto = dupTramos.length > 0 && dupForm.asignacion_ids.length === dupTramos.length;

  if (!cargando && empresa && !empresa.gestionar_zonas) {
    return <Navigate to="/admin/solicitudes" replace />;
  }

  return (
    <div className="space-y-6" data-testid="zonas-planificacion-page">
      <PageHeader
        icon={MapPin}
        title="Zonas de trabajo"
        subtitle="Planifica dónde trabaja cada empleado por horas (barra, sala, habitaciones…)"
        sectionKey="zonas"
        brandGradient={false}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setFecha((f) => sumarDias(f, -1))} className="rounded-lg text-white hover:bg-white/20">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-40 rounded-lg border-white/30 bg-white/20 text-white [color-scheme:dark]"
            data-testid="zonas-fecha-input"
          />
          <Button variant="ghost" size="icon" onClick={() => setFecha((f) => sumarDias(f, 1))} className="rounded-lg text-white hover:bg-white/20">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" onClick={() => setFecha(hoyISO())} className="rounded-lg text-white hover:bg-white/20">
            Hoy
          </Button>
          <CtaButton
            onClick={publicarDia}
            disabled={publicando}
            className="gap-1.5 rounded-lg"
          >
            <Upload className="h-4 w-4" />
            {publicando ? "Publicando…" : "Publicar"}
          </CtaButton>
        </div>
      </PageHeader>

      {solCambio.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4">
          <p className="text-sm font-semibold text-amber-900">Solicitudes de cambio de zona ({solCambio.length})</p>
          <ul className="mt-3 space-y-2">
            {solCambio.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{s.usuario}</span>
                  <span className="ml-2 text-zinc-500">{s.tipo} · {fmtFecha(s.fecha_inicio)}</span>
                  {s.usuario_intercambio_nombre && <span className="ml-2 text-violet-700">↔ {s.usuario_intercambio_nombre}</span>}
                  {s.zona_destino_nombre && <span className="ml-2 text-violet-700">→ {s.zona_destino_nombre}</span>}
                  <p className="text-xs text-zinc-500">{s.motivo}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => revisarCambioZona(s.id, "aprobar")}>Aprobar</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => revisarCambioZona(s.id, "rechazar")}>Rechazar</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Tabs defaultValue="semanal" className="space-y-4">
        <TabsList className="rounded-xl bg-fuchsia-50 p-1">
          <TabsTrigger value="semanal" className="gap-1.5 rounded-lg data-[state=active]:bg-white">
            <LayoutGrid className="h-4 w-4" />
            Vista semanal
          </TabsTrigger>
          <TabsTrigger value="plan" className="gap-1.5 rounded-lg data-[state=active]:bg-white">
            <LayoutGrid className="h-4 w-4" />
            Planificación del día
          </TabsTrigger>
          {esAdmin && (
            <TabsTrigger value="catalogo" className="gap-1.5 rounded-lg data-[state=active]:bg-white" onClick={cargarZonas}>
              <MapPin className="h-4 w-4" />
              Catálogo de zonas
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="semanal" className="space-y-4">
          <ZonasSemanal
            ref={semanalRef}
            onPublicar={() => { cargarPlan(); cargarAcuses(); }}
            onEditTramo={(emp, tramo, fechaDia) => abrirAsignacion(emp, tramo, null, fechaDia)}
            onAddTramo={(emp, fechaDia) => abrirAsignacion(emp, null, null, fechaDia)}
            onDuplicateTramo={(emp, ids, fechaDia, empleados) => abrirDuplicar(emp, ids, fechaDia, empleados)}
            onDuplicateDia={(emp, fechaDia, empleados) => abrirDuplicar(emp, null, fechaDia, empleados)}
            onAssignFromCobertura={abrirAsignacionDesdeZona}
            onEditTramoFromCobertura={editarTramoDesdeCobertura}
          />
        </TabsContent>

        <TabsContent value="plan" className="space-y-4">
          {plan ? (
            <>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 border-fuchsia-200 bg-white text-fuchsia-700 hover:bg-fuchsia-50"
                  onClick={abrirDuplicarDia}
                  data-testid="duplicar-dia-completo-btn"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Duplicar todo el día
                </Button>
              </div>
              <ZonasPlanDia
                plan={plan}
                onEditTramo={abrirAsignacion}
                onAddTramo={(emp) => abrirAsignacion(emp)}
                onDuplicateTramo={(emp, ids) => abrirDuplicar(emp, ids, fecha, plan?.empleados)}
                onDuplicateDia={(emp) => abrirDuplicar(emp, null, fecha, plan?.empleados)}
                onAssignFromCobertura={abrirAsignacionDesdeZona}
                onEditTramoFromCobertura={editarTramoDesdeCobertura}
              />
            </>
          ) : (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-fuchsia-200 border-t-fuchsia-600" />
            </div>
          )}
        </TabsContent>

        {esAdmin && (
          <TabsContent value="catalogo" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => abrirZona()} className="gap-1.5 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700">
                <Plus className="h-4 w-4" />
                Nueva zona
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {zonas.filter((z) => z.activo).map((z) => (
                <div
                  key={z.id}
                  className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
                  style={{ borderLeftWidth: 4, borderLeftColor: z.color }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-zinc-900">{z.nombre}</p>
                      {z.descripcion && <p className="mt-1 text-sm text-zinc-500">{z.descripcion}</p>}
                      {z.centro && <Badge variant="outline" className="mt-2 text-[10px]">{z.centro}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => abrirZona(z)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => eliminarZona(z)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!zonas.filter((z) => z.activo).length && (
              <p className="rounded-xl border border-dashed border-zinc-200 py-12 text-center text-sm text-zinc-400">
                Ejemplos: Barra, Sala, Terraza, Cocina, Habitaciones…
              </p>
            )}
          </TabsContent>
        )}
      </Tabs>

      {acusesPendientes.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-4" data-testid="acuses-pendientes-panel">
          <div className="flex items-center gap-2">
            <UserX className="h-4 w-4 text-rose-700" />
            <p className="text-sm font-semibold text-rose-900">
              Acuse de recibo pendiente ({acusesPendientes.length})
            </p>
          </div>
          <p className="mt-1 text-xs text-rose-800/80">
            Estos trabajadores tienen cambios publicados y aún no han confirmado que los han visto.
          </p>
          <ul className="mt-3 space-y-2">
            {acusesPendientes.map((p) => (
              <li key={p.pendiente_id} className="rounded-lg border border-rose-100 bg-white px-3 py-2.5 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium text-zinc-900">{p.nombre}</span>
                    {p.departamento && (
                      <Badge variant="outline" className="ml-2 text-[10px]">{p.departamento}</Badge>
                    )}
                    <span className="ml-2 text-rose-700">
                      {p.num_cambios} cambio{p.num_cambios !== 1 ? "s" : ""} sin confirmar
                    </span>
                  </div>
                  {p.ultima_publicacion && (
                    <span className="text-xs text-zinc-500">
                      Última publicación: {new Date(p.ultima_publicacion).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  )}
                </div>
                {p.cambios?.length > 0 && (
                  <ul className="mt-2 space-y-1 border-t border-zinc-100 pt-2 text-xs text-zinc-600">
                    {p.cambios.slice(0, 4).map((c) => (
                      <li key={c.id}>
                        {c.tipo === "creada" && "Nueva"}
                        {c.tipo === "modificada" && "Modificada"}
                        {c.tipo === "eliminada" && "Eliminada"}
                        {": "}
                        {fmtFecha(c.fecha)} · {c.zona_nombre} {c.hora_inicio && `${c.hora_inicio}–${c.hora_fin}`}
                      </li>
                    ))}
                    {p.cambios.length > 4 && (
                      <li className="text-zinc-400">+{p.cambios.length - 4} más…</li>
                    )}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={zonaOpen} onOpenChange={setZonaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editZona ? "Editar zona" : "Nueva zona"}</DialogTitle>
            <DialogDescription>Ubicación dentro del establecimiento con color para la planificación visual.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre</Label>
              <Input value={zonaForm.nombre} onChange={(e) => setZonaForm({ ...zonaForm, nombre: e.target.value })} placeholder="Barra, Sala, Habitación 101…" />
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Input value={zonaForm.descripcion} onChange={(e) => setZonaForm({ ...zonaForm, descripcion: e.target.value })} />
            </div>
            <div>
              <Label>Color</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {COLORES_ZONA.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`h-8 w-8 rounded-lg ring-2 ring-offset-2 ${zonaForm.color === c ? "ring-fuchsia-500" : "ring-transparent"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setZonaForm({ ...zonaForm, color: c })}
                  />
                ))}
                <Input type="color" value={zonaForm.color} onChange={(e) => setZonaForm({ ...zonaForm, color: e.target.value })} className="h-8 w-12 p-0" />
              </div>
            </div>
            {centros.length > 0 && (
              <div>
                <Label>Centro (opcional)</Label>
                <Select value={zonaForm.centro_id || "none"} onValueChange={(v) => setZonaForm({ ...zonaForm, centro_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Todos los centros" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin centro específico</SelectItem>
                    {centros.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZonaOpen(false)}>Cancelar</Button>
            <Button onClick={guardarZona} className="bg-fuchsia-600 hover:bg-fuchsia-700">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={asigOpen} onOpenChange={setAsigOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editAsig ? "Editar tramo" : "Asignar zona"}</DialogTitle>
            <DialogDescription>
              El trabajador verá este tramo en «Zonas» y recibirá una notificación.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!empleadoAsig && (
              <div>
                <Label>Trabajador</Label>
                <Select
                  value={asigForm.usuario_id || "none"}
                  onValueChange={(v) => {
                    if (v === "none") {
                      setEmpleadoAsig(null);
                      setAsigForm({ ...asigForm, usuario_id: "" });
                      return;
                    }
                    const emp = resolverEmpleado(empleadosAsignables, v, asigForm.fecha);
                    setEmpleadoAsig(emp);
                    setAsigForm({ ...asigForm, usuario_id: v });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Selecciona trabajador" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Selecciona trabajador —</SelectItem>
                    {empleadosAsignables.map((e) => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {empleadoAsig?.horario && (
              <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2 text-sm">
                <p className="text-xs font-semibold text-sky-800">Horario del trabajador</p>
                {empleadoAsig.horario.ausencia ? (
                  <p className="mt-1 text-amber-700">{empleadoAsig.horario.ausencia.label}</p>
                ) : empleadoAsig.horario.franjas?.length ? (
                  <p className="mt-1 text-sky-900">
                    {empleadoAsig.horario.franjas.map((f) => `${f.inicio} – ${f.fin}`).join("  ·  ")}
                    {empleadoAsig.horario.turno_nombre && ` (${empleadoAsig.horario.turno_nombre})`}
                  </p>
                ) : (
                  <p className="mt-1 text-zinc-500">Día no laborable</p>
                )}
              </div>
            )}
            <div>
              <Label>Zona</Label>
              <Select value={asigForm.zona_id} onValueChange={(v) => setAsigForm({ ...asigForm, zona_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona zona" /></SelectTrigger>
                <SelectContent>
                  {(plan?.zonas || zonas).filter((z) => z.activo !== false).map((z) => (
                    <SelectItem key={z.id} value={String(z.id)}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: z.color }} />
                        {z.nombre}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Inicio</Label>
                <Input type="time" value={asigForm.hora_inicio} onChange={(e) => setAsigForm({ ...asigForm, hora_inicio: e.target.value })} />
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="time" value={asigForm.hora_fin} onChange={(e) => setAsigForm({ ...asigForm, hora_fin: e.target.value })} />
              </div>
            </div>
            {avisoHorario && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                data-testid="aviso-fuera-horario"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  {avisoHorario.tipo === "ausencia" ? "Ausencia" : "Fuera del horario"}
                </p>
                <p className="mt-1">{avisoHorario.mensaje}</p>
                <p className="mt-1 text-xs text-amber-800/80">
                  Al guardar se te pedirá confirmación para asignar igualmente.
                </p>
              </div>
            )}
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={asigForm.notas} onChange={(e) => setAsigForm({ ...asigForm, notas: e.target.value })} placeholder="Ej. cubrir descanso de María" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {editAsig ? (
              <Button variant="destructive" onClick={() => { setAsigOpen(false); eliminarAsignacion(editAsig); }}>
                Eliminar
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAsigOpen(false)}>Cancelar</Button>
              <Button onClick={() => guardarAsignacion()} className="bg-fuchsia-600 hover:bg-fuchsia-700">Guardar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmFueraOpen} onOpenChange={setConfirmFueraOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tramo fuera del horario</AlertDialogTitle>
            <AlertDialogDescription>{confirmFueraMsg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={(e) => {
                e.preventDefault();
                guardarAsignacion(true);
              }}
              data-testid="confirmar-fuera-horario-btn"
            >
              Asignar igualmente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{esDuplicarDiaCompleto ? "Duplicar día completo" : "Duplicar tramos"}</DialogTitle>
            <DialogDescription>
              Copia zonas y horarios a otro día o rango. Opcionalmente, asigna el mismo plan a otros trabajadores.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Trabajador origen</Label>
              <Input value={dupForm.empleado_nombre} disabled className="bg-zinc-50" />
            </div>
            <div>
              <Label>Día origen</Label>
              <Input type="date" value={dupForm.fecha_origen} onChange={(e) => setDupForm({ ...dupForm, fecha_origen: e.target.value })} />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <Label>Tramos a copiar</Label>
                {dupTramos.length > 1 && (
                  <button
                    type="button"
                    className="text-[10px] font-medium text-fuchsia-600 hover:underline"
                    onClick={seleccionarTodosTramosDup}
                  >
                    Seleccionar todos ({dupTramos.length})
                  </button>
                )}
              </div>
              <div className="mt-2 max-h-32 space-y-1 overflow-y-auto rounded-lg border border-zinc-200 p-2">
                {(dupTramos.length ? dupTramos : []).map((t) => (
                  <label key={t.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-zinc-50">
                    <input
                      type="checkbox"
                      checked={dupForm.asignacion_ids.includes(t.id)}
                      onChange={() => toggleTramoDup(t.id)}
                    />
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.zona_color }} />
                    <span>{t.zona_nombre}</span>
                    <span className="text-zinc-400">{t.hora_inicio}–{t.hora_fin}</span>
                  </label>
                ))}
                {!dupTramos.length && (
                  <p className="text-xs text-zinc-400">Sin tramos en el día origen</p>
                )}
              </div>
            </div>
            <div>
              <Label>Copiar también a otros trabajadores (opcional)</Label>
              <p className="mb-2 text-[10px] text-zinc-500">
                Si no seleccionas ninguno, solo se copia al trabajador origen en las fechas destino.
              </p>
              <div className="max-h-36 space-y-1 overflow-y-auto rounded-lg border border-zinc-200 p-2">
                {empleadosDupLista
                  .filter((e) => String(e.id) !== dupForm.usuario_id)
                  .map((e) => (
                    <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-zinc-50">
                      <input
                        type="checkbox"
                        checked={dupForm.usuarios_destino_ids.includes(e.id)}
                        onChange={() => toggleDestinoDup(e.id)}
                      />
                      <span>{e.nombre}</span>
                      {e.departamento && <span className="text-xs text-zinc-400">{e.departamento}</span>}
                    </label>
                  ))}
                {empleadosDupLista.filter((e) => String(e.id) !== dupForm.usuario_id).length === 0 && (
                  <p className="text-xs text-zinc-400">No hay otros trabajadores en el ámbito</p>
                )}
              </div>
            </div>
            <div>
              <Label>Destino</Label>
              <div className="mt-2 flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="radio" name="modo_destino" checked={dupForm.modo_destino === "dia"} onChange={() => setDupForm({ ...dupForm, modo_destino: "dia" })} />
                  Un día
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="modo_destino" checked={dupForm.modo_destino === "rango"} onChange={() => setDupForm({ ...dupForm, modo_destino: "rango" })} />
                  Rango
                </label>
              </div>
            </div>
            {dupForm.modo_destino === "dia" ? (
              <div>
                <Label>Fecha destino</Label>
                <Input type="date" value={dupForm.fecha_destino} onChange={(e) => setDupForm({ ...dupForm, fecha_destino: e.target.value })} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Desde</Label>
                  <Input type="date" value={dupForm.fecha_destino_desde} onChange={(e) => setDupForm({ ...dupForm, fecha_destino_desde: e.target.value })} />
                </div>
                <div>
                  <Label>Hasta</Label>
                  <Input type="date" value={dupForm.fecha_destino_hasta} onChange={(e) => setDupForm({ ...dupForm, fecha_destino_hasta: e.target.value })} />
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-zinc-600">
              <input type="checkbox" checked={dupForm.sobrescribir} onChange={(e) => setDupForm({ ...dupForm, sobrescribir: e.target.checked })} />
              Reemplazar tramos iguales si ya existen
            </label>
            {dupPreview && (
              <div className="rounded-lg border border-sky-100 bg-sky-50 p-2 text-xs text-sky-900">
                <p>{dupPreview.duplicables} tramo(s) se duplicarán sin incidencias</p>
                {dupPreview.incidencias?.length > 0 && (
                  <p className="mt-1 text-amber-800">{dupPreview.incidencias.length} con incidencias (vacaciones, baja, fuera de horario…)</p>
                )}
              </div>
            )}
            {dupResult?.omitidas?.length > 0 && (
              <div className="max-h-32 overflow-y-auto rounded-lg border border-amber-100 bg-amber-50 p-2 text-xs text-amber-900">
                <p className="font-semibold">Omitidos ({dupResult.omitidas.length}):</p>
                <ul className="mt-1 space-y-0.5">
                  {dupResult.omitidas.slice(0, 10).map((o, i) => (
                    <li key={i}>{fmtFecha(o.fecha)}{o.tramo ? ` · ${o.tramo}` : ""} — {o.motivo}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>Cerrar</Button>
            <Button
              onClick={() => {
                if (!dupForm.usuario_id || !dupForm.asignacion_ids.length) {
                  toast.error("Selecciona al menos un tramo");
                  return;
                }
                duplicarTramos();
              }}
              className="bg-fuchsia-600 hover:bg-fuchsia-700"
              data-testid="confirmar-duplicar-btn"
            >
              Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dupDiaOpen} onOpenChange={setDupDiaOpen}>
        <DialogContent className="max-w-md" data-testid="duplicar-dia-dialog">
          <DialogHeader>
            <DialogTitle>Duplicar todo el día</DialogTitle>
            <DialogDescription>
              Copia todas las zonas de {fmtFecha(fecha)} a otro día o periodo. Se revisan vacaciones, bajas y horarios.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <Label>Día origen</Label>
              <Input type="date" value={dupDiaForm.fecha_origen} disabled className="mt-1 bg-zinc-50" />
            </div>
            <div>
              <Label>Destino</Label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input type="radio" name="modo_destino_dia" checked={dupDiaForm.modo_destino === "dia"} onChange={() => setDupDiaForm({ ...dupDiaForm, modo_destino: "dia" })} />
                  Un día
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="modo_destino_dia" checked={dupDiaForm.modo_destino === "rango"} onChange={() => setDupDiaForm({ ...dupDiaForm, modo_destino: "rango" })} />
                  Rango
                </label>
              </div>
            </div>
            {dupDiaForm.modo_destino === "dia" ? (
              <div>
                <Label>Fecha destino</Label>
                <Input type="date" value={dupDiaForm.fecha_destino} onChange={(e) => setDupDiaForm({ ...dupDiaForm, fecha_destino: e.target.value })} className="mt-1" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Desde</Label>
                  <Input type="date" value={dupDiaForm.fecha_destino_desde} onChange={(e) => setDupDiaForm({ ...dupDiaForm, fecha_destino_desde: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Hasta</Label>
                  <Input type="date" value={dupDiaForm.fecha_destino_hasta} onChange={(e) => setDupDiaForm({ ...dupDiaForm, fecha_destino_hasta: e.target.value })} className="mt-1" />
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-zinc-600">
              <input type="checkbox" checked={dupDiaForm.sobrescribir} onChange={(e) => setDupDiaForm({ ...dupDiaForm, sobrescribir: e.target.checked })} />
              Reemplazar tramos iguales si ya existen
            </label>
            {dupPreview && (
              <div className="rounded-lg border border-sky-100 bg-sky-50 p-2 text-xs text-sky-900">
                <p><strong>{dupPreview.duplicables}</strong> tramo(s) listos · <strong>{plan?.empleados?.reduce((n, e) => n + e.tramos.length, 0) || 0}</strong> en origen</p>
                {dupPreview.incidencias?.length > 0 && (
                  <p className="mt-1 text-amber-800">{dupPreview.incidencias.length} tramo(s) con incidencias</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupDiaOpen(false)}>Cancelar</Button>
            <Button onClick={duplicarDiaCompleto} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="confirmar-duplicar-dia-btn">
              Duplicar día
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={dupIncidenciasOpen}
        onOpenChange={(open) => {
          setDupIncidenciasOpen(open);
          if (!open) setDupPendiente(null);
        }}
      >
        <AlertDialogContent data-testid="duplicar-incidencias-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Incidencias detectadas</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-zinc-600">
                <p>
                  {dupPreview?.duplicables || 0} tramo(s) se duplicarán correctamente.
                  {" "}
                  {dupPreview?.incidencias?.length || 0} tienen incidencias (vacaciones, baja, asuntos propios, fuera de horario o día no laborable):
                </p>
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-amber-100 bg-amber-50 p-2 text-xs text-amber-900">
                  {(dupPreview?.incidencias || []).slice(0, 20).map((inc, i) => (
                    <li key={i}>
                      <strong>{inc.usuario}</strong> · {fmtFecha(inc.fecha)} · {inc.tramo} — {inc.motivo}
                    </li>
                  ))}
                </ul>
                <p>¿Deseas duplicar también los tramos con incidencia?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => confirmarDuplicarConIncidencias(false)}>
              Solo sin incidencias ({dupPreview?.duplicables || 0})
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmarDuplicarConIncidencias(true)} className="bg-amber-600 hover:bg-amber-700">
              Duplicar igualmente ({dupPreview?.incidencias?.length || 0})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
